import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import QRCode from 'qrcode'
import { ApiClientError } from '@/lib/api/client'
import { DonorAvatar } from './DonorAvatar'
import { DEFAULT_AVATAR } from './avatar-catalog'
import { equipAvatarPiece, type AvatarPiece } from './avatar-custom'
import {
  createAvatarItemPurchase,
  fetchAvatarItemPurchase,
  fetchAvatarPurchase,
  type AvatarPurchase,
} from './avatar-purchases-api'
import styles from './AvatarPurchaseDialog.module.css'

const price = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const message = (error: unknown) =>
  error instanceof ApiClientError
    ? Object.values(error.errors ?? {}).flat()[0] || error.message
    : 'Não foi possível consultar a compra. Tente novamente.'

type Props = {
  campaignId: number
  piece: AvatarPiece
  institutionName: string
  salesOpen: boolean
  onPurchased: () => Promise<AvatarPiece>
  onUse: (piece: AvatarPiece) => void
  onClose: () => void
}

export function AvatarPurchaseDialog({
  campaignId,
  piece,
  institutionName,
  salesOpen,
  onPurchased,
  onUse,
  onClose,
}: Props) {
  const [purchase, setPurchase] = useState<AvatarPurchase | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [qr, setQr] = useState('')
  const [copied, setCopied] = useState(false)
  const [owned, setOwned] = useState<AvatarPiece | null>(null)
  const [retry, setRetry] = useState(0)
  const dialog = useRef<HTMLDialogElement>(null)
  const active = useRef(false)
  const locked = useRef(false)
  const requestId = useRef<string | null>(null)
  const lastPurchase = useRef<AvatarPurchase | null>(null)
  const notified = useRef<string | null>(null)
  const callbacks = useRef({ onPurchased, onClose })
  useEffect(() => {
    callbacks.current = { onPurchased, onClose }
  }, [onPurchased, onClose])

  const deliver = useCallback(async (next: AvatarPurchase) => {
    if (next.status !== 'paid' || !next.delivered_at || notified.current === next.id)
      return
    notified.current = next.id
    try {
      const acquired = await callbacks.current.onPurchased()
      if (active.current) setOwned(acquired)
    } catch (cause) {
      notified.current = null
      if (active.current) setError(message(cause))
    }
  }, [])
  const accept = useCallback(
    (next: AvatarPurchase | null) => {
      if (
        lastPurchase.current?.status === 'paid' &&
        next?.id === lastPurchase.current.id &&
        next.status !== 'paid'
      )
        return
      lastPurchase.current = next
      setPurchase(next)
      if (next) void deliver(next)
    },
    [deliver]
  )

  useEffect(() => {
    active.current = true
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const node = dialog.current
    node?.showModal()
    return () => {
      active.current = false
      node?.close()
      previousFocus?.focus()
    }
  }, [])
  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError('')
    void fetchAvatarItemPurchase(campaignId, piece.id, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) accept(data.purchase)
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(message(cause))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [campaignId, piece.id, retry, accept])
  useEffect(() => {
    setQr('')
    if (!purchase?.pix_key || purchase.status !== 'pending') return
    let cancelled = false
    void QRCode.toDataURL(purchase.pix_key, { width: 240, margin: 2 })
      .then((url) => {
        if (!cancelled) setQr(url)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [purchase?.pix_key, purchase?.status])
  const purchaseId = purchase?.id
  const purchaseStatus = purchase?.status
  useEffect(() => {
    if (
      !purchaseId ||
      !purchaseStatus ||
      !['creating', 'creation_uncertain', 'pending'].includes(purchaseStatus)
    )
      return
    const controller = new AbortController()
    const started = Date.now()
    let timer: number | undefined
    const id = purchaseId
    async function poll() {
      if (controller.signal.aborted || Date.now() - started >= 300_000) return
      if (document.visibilityState !== 'hidden' && !locked.current) {
        locked.current = true
        try {
          const next = await fetchAvatarPurchase(campaignId, id, controller.signal)
          if (controller.signal.aborted) return
          accept(next)
          if (['paid', 'expired'].includes(next.status)) return
        } catch {
          /* Keep the existing payment visible; manual refresh remains available. */
        } finally {
          locked.current = false
        }
      }
      if (!controller.signal.aborted) timer = window.setTimeout(() => void poll(), 5000)
    }
    timer = window.setTimeout(() => void poll(), 5000)
    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [campaignId, purchaseId, purchaseStatus, accept])

  async function buy() {
    if (locked.current || loading || purchase || !salesOpen) return
    locked.current = true
    setBusy(true)
    setError('')
    requestId.current ??= crypto.randomUUID()
    try {
      const next = await createAvatarItemPurchase(campaignId, piece.id, requestId.current)
      if (active.current) accept(next)
    } catch (cause) {
      if (active.current) setError(message(cause))
      if (cause instanceof ApiClientError && cause.status >= 400 && cause.status < 500)
        requestId.current = null
    } finally {
      locked.current = false
      if (active.current) setBusy(false)
    }
  }
  async function refresh() {
    if (!purchase || locked.current) return
    locked.current = true
    setBusy(true)
    setError('')
    try {
      const next = await fetchAvatarPurchase(campaignId, purchase.id)
      if (active.current) accept(next)
    } catch (cause) {
      if (active.current) setError(message(cause))
    } finally {
      locked.current = false
      if (active.current) setBusy(false)
    }
  }
  return createPortal(
    <dialog
      ref={dialog}
      className={styles.dialog}
      aria-labelledby="avatar-purchase-title"
      onCancel={(event) => {
        event.preventDefault()
        if (!busy) onClose()
      }}
    >
      <header className={styles.header}>
        <span>COLEÇÃO DA SUA CAUSA</span>
        <button
          type="button"
          disabled={busy}
          onClick={onClose}
          aria-label="Fechar compra"
        >
          ×
        </button>
      </header>
      <div className={styles.content}>
        <div className={styles.preview}>
          <DonorAvatar
            config={equipAvatarPiece(DEFAULT_AVATAR, purchase?.item ?? piece)}
            transparent
          />
        </div>
        <h2 id="avatar-purchase-title">{purchase?.item_name ?? piece.name}</h2>
        <strong className={styles.price}>
          {price(purchase?.amount_cents ?? piece.price_cents ?? 0)}
        </strong>
        <p>
          {(purchase?.item ?? piece).campaign_id === null ? (
            <>
              Esta peça faz parte do catálogo global de avatar. A compra segue a
              distribuição financeira da plataforma.
            </>
          ) : (
            <>
              Ao comprar esta peça, você apoia <strong>{institutionName}</strong>. A
              compra segue a distribuição financeira da plataforma.
            </>
          )}
        </p>
        <p className={styles.note}>
          Após a confirmação do Pix, a peça fica na sua conta para usar e trocar quando
          quiser.
        </p>
        {loading && <p role="status">Consultando sua compra…</p>}
        {error && (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        )}
        {!loading && !purchase && !error && (
          <button
            className={styles.primary}
            type="button"
            disabled={busy || !salesOpen}
            onClick={() => void buy()}
          >
            {busy ? 'Preparando Pix…' : `Comprar por ${price(piece.price_cents ?? 0)}`}
          </button>
        )}
        {!loading && !purchase && error && (
          <button type="button" onClick={() => setRetry((value) => value + 1)}>
            Tentar novamente
          </button>
        )}
        {!salesOpen && !purchase && <p>As compras estão indisponíveis nesta campanha.</p>}
        {purchase?.status === 'pending' && (
          <div className={styles.payment}>
            <p role="status">Aguardando pagamento</p>
            {qr && (
              <img
                src={qr}
                width="240"
                height="240"
                alt="QR Code Pix da compra da peça"
              />
            )}
            <label>
              Pix copia e cola
              <textarea readOnly value={purchase.pix_key ?? ''} />
            </label>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard
                  .writeText(purchase.pix_key ?? '')
                  .then(() => setCopied(true))
                  .catch(() =>
                    setError('Não foi possível copiar. Selecione o código acima.')
                  )
              }}
            >
              {copied ? 'Código copiado' : 'Copiar código Pix'}
            </button>
            {purchase.expires_at && (
              <small>
                Prazo de exibição: {new Date(purchase.expires_at).toLocaleString('pt-BR')}
              </small>
            )}
          </div>
        )}
        {purchase?.status === 'creation_uncertain' && (
          <p role="status">
            Estamos aguardando a confirmação da cobrança. Consulte novamente ou contate o
            suporte antes de fazer outro pagamento.
          </p>
        )}
        {purchase?.status === 'creating' && <p role="status">Preparando sua cobrança…</p>}
        {purchase?.status === 'expired' && (
          <p>
            O prazo de exibição deste Pix terminou. Se você pagou, consulte a confirmação.
            Para retomar uma compra não paga, contate o suporte.
          </p>
        )}
        {purchase && !owned && (
          <button type="button" disabled={busy || loading} onClick={() => void refresh()}>
            {busy ? 'Consultando…' : 'Consultar confirmação'}
          </button>
        )}
        {owned && (
          <div className={styles.success}>
            <p role="status">Pagamento confirmado! Esta peça já está na sua coleção.</p>
            <button className={styles.primary} type="button" onClick={() => onUse(owned)}>
              Usar no meu avatar
            </button>
          </div>
        )}
      </div>
    </dialog>,
    document.body
  )
}
